/**
 * File path canonicalisation.
 *
 * Vestra agents emit file paths in two flavours:
 *   - Absolute sandbox paths from server-side TaskOutput rows:
 *       /app/workspace/files/foo.md
 *   - Relative paths from Write/Edit tool inputs (whatever the agent typed):
 *       files/foo.md
 *
 * Comparing these with `===` silently fails, which produced the "chip-click
 * always opens the same file" bug. Canonicalise on ingest so every downstream
 * lookup site can use plain exact-match.
 *
 * Canonical form = relative-to-workspace-root, no leading slash.
 *   "/app/workspace/files/foo.md"  -> "files/foo.md"
 *   "/app/workspace/"              -> ""
 *   "/app/workspace"               -> ""
 *   "/files/foo.md"                -> "files/foo.md"
 *   "files/foo.md"                 -> "files/foo.md"
 *   "" | null | undefined          -> ""
 *
 * Note: case-sensitive by design — the sandbox is Linux, paths are
 * case-sensitive there. Do NOT add `.toLowerCase()`; "Files/Foo.md" and
 * "files/foo.md" are different files in the sandbox.
 */

const SANDBOX_PREFIX = "/app/workspace";

export function toCanonicalPath(p: string | null | undefined): string {
  if (!p) return "";
  let path = p;
  if (path === SANDBOX_PREFIX || path.startsWith(`${SANDBOX_PREFIX}/`)) {
    path = path.slice(SANDBOX_PREFIX.length);
  }
  while (path.startsWith("/")) path = path.slice(1);
  return path;
}

/**
 * Is this a file the user actually keeps — a task DELIVERABLE?
 *
 * The single definition (matching the server's output watcher, which watches
 * `/app/workspace/files/**` exclusively): a deliverable lives under the user's
 * `files/` directory. Everything else — a skill's reference/example files under
 * `.claude/abilities/…`, scratch writes to the workspace root or `/tmp` — is NOT
 * a deliverable and must never appear in the "Files in this task" shelf. Keeping
 * this aligned with the server prevents the shelf from diverging from the
 * Computer "This task" panel.
 *
 * Operates on a CANONICAL path (see {@link toCanonicalPath}):
 *   "files/foo.md"           -> true
 *   "files/reports/q2.xlsx"  -> true
 *   ".claude/abilities/x.md" -> false   (skill scaffolding)
 *   "report.html"            -> false   (workspace-root scratch)
 */
export function isUserDeliverablePath(canonicalPath: string): boolean {
  return canonicalPath.startsWith("files/");
}
